/* DYBENDS V6 storefront behaviour. Vanilla custom elements, no dependencies.
   Dialogs (menu, search, cart, zoom) are native <dialog> elements: focus trapping, Escape and
   inert backgrounds come from the browser. No window scroll listeners: IntersectionObserver only. */
(() => {
  const DYB = window.DYB || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- helpers ---------- */
  const formatMoney = (cents, fmt = DYB.moneyFormat || '€{{amount}}') => {
    const format = String(fmt).replace(/<[^>]*>/g, ''); // stores often wrap the format in <span class=money>
    const value = Number(cents || 0) / 100;
    const withDelimiters = (n, decimals, thousands = ',', decimal = '.') => {
      const [whole, frac] = n.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2);
      }
    });
  };
  const announce = (text) => {
    const region = document.querySelector('dialog[open] [data-live]') || document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = text; }, 60);
  };
  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(data.description || data.message || 'Cart error'), { data });
    return data;
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const mainCartSectionId = () => {
    const el = document.querySelector('[data-main-cart]');
    const wrap = el && el.closest('.shopify-section');
    return wrap ? wrap.id.replace('shopify-section-', '') : null;
  };
  const sectionsToRender = () => ['cart-drawer', mainCartSectionId()].filter(Boolean);

  /* ---------- modal dialogs ---------- */
  const lockState = () => {
    const anyOpen = !!document.querySelector('dialog[open]');
    document.documentElement.classList.toggle('no-scroll', anyOpen);
  };
  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    lockState();
    if (opener && opener.hasAttribute('aria-haspopup')) opener.setAttribute('aria-expanded', 'true');
  };
  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.open);
      if (dialog) {
        event.preventDefault();
        openDialog(dialog, opener);
        const field = dialog.querySelector('[data-autofocus]');
        if (field) window.requestAnimationFrame(() => field.focus());
      }
      return;
    }
    const closer = event.target.closest('dialog [data-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // a click on the backdrop lands on the dialog element itself
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === ' ' && event.target.matches('a[role="button"]')) { event.preventDefault(); event.target.click(); }
  });
  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    lockState();
    document.querySelectorAll(`[data-open="${dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (dialog._opener && dialog._opener.hasAttribute('aria-haspopup')) dialog._opener.setAttribute('aria-expanded', 'false');
    if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus({ preventScroll: true });
  }, true);

  /* ---------- header ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-overlay')) {
        const sentinel = document.createElement('div');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:48px;pointer-events:none;';
        document.body.prepend(sentinel);
        this.observer = new IntersectionObserver(([entry]) => this.classList.toggle('is-scrolled', !entry.isIntersecting));
        this.observer.observe(sentinel);
      }
      this.dropdowns = [...this.querySelectorAll('[data-dropdown]')];
      this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.dropdowns.forEach((d) => {
        d.addEventListener('toggle', () => { if (d.open) this.dropdowns.forEach((o) => { if (o !== d) o.open = false; }); });
        d.addEventListener('mouseleave', () => { if (this.fine.matches && !d.contains(document.activeElement)) d.open = false; });
        d.addEventListener('focusout', (e) => { if (!d.contains(e.relatedTarget)) d.open = false; });
      });
      this.onKey = (e) => {
        if (e.key !== 'Escape') return;
        const open = this.dropdowns.find((d) => d.open);
        if (open) { open.open = false; open.querySelector('summary').focus(); }
      };
      document.addEventListener('keydown', this.onKey);
      this.onDoc = (e) => { this.dropdowns.forEach((d) => { if (d.open && !d.contains(e.target)) d.open = false; }); };
      document.addEventListener('click', this.onDoc);
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDoc);
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ---------- predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      this.url = this.dataset.url || '/search/suggest';
      this.timer = null;
      this.controller = null;
      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.search(this.input.value.trim()), 200);
      });
      this.input.addEventListener('keydown', (e) => this.navigate(e));
    }
    async search(q) {
      if (this.controller) this.controller.abort();
      if (!q) { this.results.innerHTML = ''; this.input.setAttribute('aria-expanded', 'false'); return; }
      this.controller = new AbortController();
      try {
        const params = new URLSearchParams({ q, section_id: 'predictive-search', 'resources[type]': 'product,page,collection', 'resources[limit]': '6', 'resources[options][prefix]': 'last' });
        const response = await fetch(`${this.url}?${params}`, { signal: this.controller.signal });
        const html = await response.text();
        const node = parseHTML(html).querySelector('[data-predictive-results]');
        this.results.innerHTML = node ? node.outerHTML : '';
        const options = this.results.querySelectorAll('[role="option"]').length;
        this.input.setAttribute('aria-expanded', options ? 'true' : 'false');
        const status = this.querySelector('[data-status]');
        if (status) status.textContent = (options === 1 ? status.dataset.one : status.dataset.other).replace(/\d+/, String(options));
        this.active = -1;
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
    navigate(e) {
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        if (this.active >= 0) { e.preventDefault(); options[this.active].querySelector('a').click(); }
        return;
      }
      e.preventDefault();
      this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', i === this.active ? 'true' : 'false'));
      this.input.setAttribute('aria-activedescendant', options[this.active].id);
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- cart ---------- */
  const updateCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
      if (!reduceMotion.matches) { el.classList.remove('is-bumped'); void el.offsetWidth; el.classList.add('is-bumped'); }
    });
    const template = count === 1 ? DYB.strings.cartOne : DYB.strings.cartOther;
    document.querySelectorAll('[data-cart-toggle]').forEach((el) => {
      if (template) el.setAttribute('aria-label', template.replace(/\d+/, String(count)));
    });
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle && DYB.cartType !== 'page' && !document.querySelector('[data-main-cart]')) {
          e.preventDefault();
          this.open(toggle);
        }
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove]');
        if (remove) changeLine(remove.dataset.remove, 0, remove);
      });
    }
    open(opener) { openDialog(this.dialog, opener); }
    render(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const fresh = parseHTML(sections['cart-drawer']).querySelector('[data-drawer-content]');
      const current = this.querySelector('[data-drawer-content]');
      if (fresh && current) current.replaceWith(fresh);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  const renderSections = (sections) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.render(sections);
    const mainId = mainCartSectionId();
    if (mainId && sections && sections[mainId]) {
      const fresh = parseHTML(sections[mainId]).querySelector('[data-main-cart]');
      const current = document.querySelector('[data-main-cart]');
      if (fresh && current) current.replaceWith(fresh);
    }
  };

  // Cart changes go one at a time, by line key (never by position, which shifts as lines go)
  let cartQueue = Promise.resolve();
  const refreshCart = async () => {
    const ids = sectionsToRender().join(',');
    const sections = await fetch(`${window.location.pathname}?sections=${ids}`).then((r) => r.json()).catch(() => null);
    if (sections) renderSections(sections);
    const cart = await fetch(`${DYB.routes.cart}.js`).then((r) => r.json()).catch(() => null);
    if (cart) updateCount(cart.item_count);
  };
  const changeLine = (key, quantity, source) => {
    const busy = source && source.closest('.line, qty-stepper');
    if (busy) busy.setAttribute('aria-busy', 'true');
    cartQueue = cartQueue.then(async () => {
      try {
        const data = await postJSON(DYB.routes.cartChange, { id: key, quantity, sections: sectionsToRender(), sections_url: window.location.pathname });
        renderSections(data.sections);
        updateCount(data.item_count);
        const restore = document.querySelector(`[data-key="${CSS.escape(key)}"] [data-qty]`) || document.querySelector('dialog[open] [data-close]');
        if (restore && document.activeElement === document.body) restore.focus({ preventScroll: true });
      } catch (err) {
        announce(DYB.strings.error);
        await refreshCart();
      }
    });
    return cartQueue;
  };
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-main-cart] [data-remove]');
    if (remove) changeLine(remove.dataset.remove, 0, remove);
  });

  class QtyStepper extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn) return;
        const min = Number(this.input.min || 0);
        const next = Math.max(min, (Number(this.input.value) || 0) + Number(btn.dataset.step));
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.input.addEventListener('change', () => this.commit());
    }
    commit() {
      if (!this.dataset.key) return; // a product form quantity: nothing to send yet
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => changeLine(this.dataset.key, Math.max(0, Number(this.input.value) || 0), this), 250);
    }
  }
  customElements.define('qty-stepper', QtyStepper);

  /* ---------- add to cart (product pages, cards, kit add-ons) ---------- */
  const addItems = async (items) => {
    const data = await postJSON(DYB.routes.cartAdd, { items, sections: sectionsToRender(), sections_url: window.location.pathname });
    renderSections(data.sections);
    try {
      const cart = await fetch(`${DYB.routes.cart}.js`).then((r) => r.json());
      updateCount(cart.item_count);
    } catch (e) { /* the items are in; the count catches up on the next page */ }
    return data;
  };
  const afterAdd = (opener) => {
    if (DYB.cartType === 'page') { window.location.href = DYB.routes.cart; return; }
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !document.querySelector('[data-main-cart]')) drawer.open(opener);
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.submit(e));
      // running total when the buy box has add-ons
      this.scope = this.closest('[data-buy-scope]');
      this.totalEl = this.scope && this.scope.querySelector('[data-addons-total]');
      if (this.totalEl) {
        const json = this.scope.querySelector('variant-pills script[type="application/json"]');
        this.variants = json ? JSON.parse(json.textContent).variants : null;
        this.basePrice = Number(this.totalEl.dataset.base || 0);
        this.scope.addEventListener('change', () => this.updateTotal());
        this.scope.addEventListener('variant:change', (e) => { this.basePrice = e.detail.variant.price; this.updateTotal(); });
        this.updateTotal();
      }
    }
    updateTotal() {
      const qty = Math.max(1, Number((this.form.querySelector('[name="quantity"]') || {}).value) || 1);
      let addons = 0;
      let count = 0;
      this.scope.querySelectorAll('[data-addon-row]').forEach((row) => {
        const select = row.querySelector('[data-addon-variant]');
        const label = row.querySelector('[data-addon-price]');
        if (select && label) label.textContent = `+${formatMoney(select.selectedOptions[0].dataset.price)}`;
      });
      this.scope.querySelectorAll('[data-addon]:checked').forEach((box) => {
        const select = box.closest('[data-addon-row]').querySelector('[data-addon-variant]');
        addons += Number(select ? select.selectedOptions[0].dataset.price : box.dataset.price);
        count += 1;
      });
      this.totalEl.hidden = count === 0;
      this.totalEl.textContent = (this.totalEl.dataset.label || 'Total %PRICE%').replace('%PRICE%', formatMoney(this.basePrice * qty + addons));
    }
    async submit(e) {
      e.preventDefault();
      const button = this.form.querySelector('[type="submit"]');
      if (!button || button.getAttribute('aria-busy') === 'true' || button.disabled) return;
      const data = new FormData(this.form);
      const items = [{ id: Number(data.get('id')), quantity: Math.max(1, Number(data.get('quantity')) || 1) }];
      const scope = this.closest('[data-buy-scope]');
      if (scope) scope.querySelectorAll('[data-addon]:checked').forEach((box) => {
        const select = box.closest('[data-addon-row]')?.querySelector('[data-addon-variant]');
        items.push({ id: Number(select ? select.value : box.value), quantity: 1 });
      });
      const label = button.querySelector('[data-label]');
      if (label && !label.dataset.default) label.dataset.default = label.textContent;
      window.clearTimeout(this.labelTimer);
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = DYB.strings.adding;
      const error = this.querySelector('[data-error]');
      if (error) error.hidden = true;
      try {
        await addItems(items);
        if (label) label.textContent = DYB.strings.added;
        button.classList.add('is-added');
        afterAdd(button);
        announce(DYB.strings.addedToCart);
      } catch (err) {
        if (error) { error.textContent = (err.data && (err.data.description || err.data.message)) || DYB.strings.error; error.hidden = false; }
        else announce(DYB.strings.error);
      } finally {
        button.removeAttribute('aria-busy');
        this.labelTimer = window.setTimeout(() => {
          button.classList.remove('is-added');
          if (label) label.textContent = button.disabled ? DYB.strings.soldOut : label.dataset.default;
        }, 1800);
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- variant picker ---------- */
  class VariantPills extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"]');
      this.product = json ? JSON.parse(json.textContent) : null;
      if (!this.product) return;
      this.scope = this.closest('[data-buy-scope]') || document;
      this.addEventListener('change', () => this.update());
    }
    selected() {
      return [...this.querySelectorAll('fieldset')].map((fs) => (fs.querySelector('input:checked') || {}).value);
    }
    update() {
      const values = this.selected();
      const variant = this.product.variants.find((v) => v.options.every((o, i) => o === values[i]));
      this.querySelectorAll('fieldset').forEach((fs, i) => {
        const legendValue = fs.querySelector('[data-selected-value]');
        if (legendValue) legendValue.textContent = values[i] || '';
      });
      const idInputs = this.scope.querySelectorAll('input[name="id"][data-variant-id]');
      const buttons = this.scope.querySelectorAll('[data-add-button]');
      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; const l = b.querySelector('[data-label]'); if (l) l.textContent = DYB.strings.unavailable; });
        return;
      }
      idInputs.forEach((i) => { i.value = variant.id; });
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        const l = b.querySelector('[data-label]');
        if (l) { l.textContent = variant.available ? DYB.strings.addToCart : DYB.strings.soldOut; l.dataset.default = DYB.strings.addToCart; }
      });
      this.scope.querySelectorAll('[data-price-amount]').forEach((el) => { el.textContent = formatMoney(variant.price); });
      this.scope.querySelectorAll('[data-price-compare]').forEach((el) => {
        el.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media) {
        const gallery = document.querySelector('media-gallery');
        if (gallery) gallery.show(variant.featured_media.id);
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }
  customElements.define('variant-pills', VariantPills);

  /* ---------- gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.counter = this.querySelector('[data-counter]');
      if (!this.track || !this.slides.length) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting && entry.intersectionRatio > 0.6) this.setCurrent(this.slides.indexOf(entry.target)); });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((s) => this.observer.observe(s));
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => this.go(this.current + Number(b.dataset.step))));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.current + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.current - 1); }
      });
      this.zoom = this.querySelector('dialog[data-zoom-dialog]');
      this.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-zoom]');
        if (!trigger || !this.zoom) return;
        const img = this.zoom.querySelector('img');
        img.src = trigger.dataset.zoom;
        img.alt = trigger.querySelector('img') ? trigger.querySelector('img').alt : '';
        openDialog(this.zoom, trigger);
      });
      this.current = 0;
    }
    setCurrent(index) {
      this.current = index;
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', i === index ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    }
    go(index) {
      const i = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[i];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      this.setCurrent(i);
    }
    show(mediaId) {
      const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (i >= 0) this.go(i);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- sticky buy bar ---------- */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(this.dataset.target || '[data-add-button]');
      const footer = document.querySelector('.footer');
      if (!target) return;
      this.state = { target: true, footer: false };
      const apply = () => {
        this.classList.toggle('is-visible', !this.state.target && !this.state.footer);
        this.toggleAttribute('inert', this.state.target || this.state.footer);
      };
      // the root reaches far below the screen, so the button counts as "in view" until it is
      // above the screen: a jump straight past it (anchor, scroll restore, fling) still registers
      // (top > 0 covers the theme editor's cross-origin iframe, which ignores rootMargin)
      this.observer = new IntersectionObserver(([entry]) => { this.state.target = entry.isIntersecting || entry.boundingClientRect.top > 0; apply(); }, { rootMargin: '0px 0px 100000px 0px' });
      this.observer.observe(target);
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => { this.state.footer = entry.isIntersecting; apply(); });
        this.footerObserver.observe(footer);
      }
      this.setAttribute('inert', '');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sticky-add]');
        if (!btn) return;
        const main = document.querySelector('[data-add-button]');
        if (main && !main.disabled) main.click();
        else if (main) main.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
      });
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  /* ---------- lamp chooser ---------- */
  class LampChooser extends HTMLElement {
    connectedCallback() {
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      // the switch only exists below 990 px; side by side, both panels always show
      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.activate(i)));
    }
    activate(index) {
      this.panels.forEach((p, i) => p.classList.toggle('is-active', i === index));
      this.tabs.forEach((t, i) => t.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
    }
  }
  customElements.define('lamp-chooser', LampChooser);

  /* ---------- colour range (Creator Lamp): swatches recolour an illustrated wash ---------- */
  class ColourRange extends HTMLElement {
    connectedCallback() {
      this.name = this.querySelector('[data-colour-name]');
      this.swatches = [...this.querySelectorAll('[data-colour]')];
      this.swatches.forEach((swatch) => swatch.addEventListener('click', () => {
        this.style.setProperty('--swatch', swatch.dataset.colour);
        this.swatches.forEach((s) => s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false'));
        if (this.name) this.name.textContent = swatch.dataset.name;
      }));
    }
  }
  customElements.define('colour-range', ColourRange);

  /* ---------- trust strip: one message at a time on phones, rotating unless paused ---------- */
  class TrustStrip extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-trust-list]');
      if (!this.list || this.list.children.length < 2 || !this.hasAttribute('data-rotate')) return;
      this.narrow = window.matchMedia('(max-width: 1099px)');
      this.still = window.matchMedia('(prefers-reduced-motion: reduce)');
      const hold = () => { this.held = true; };
      const release = () => { this.held = false; };
      this.addEventListener('pointerenter', hold);
      this.addEventListener('pointerleave', release);
      this.addEventListener('focusin', hold);
      this.addEventListener('focusout', release);
      // a visitor who swipes is reading: stop rotating for good
      this.list.addEventListener('touchstart', () => { this.stopped = true; }, { passive: true });
      this.timer = window.setInterval(() => this.next(), 4500);
    }
    disconnectedCallback() { window.clearInterval(this.timer); }
    next() {
      if (this.held || this.stopped || document.hidden || !this.narrow.matches || this.still.matches) return;
      const width = this.list.clientWidth;
      const end = this.list.scrollWidth - width;
      const to = this.list.scrollLeft + width > end + 1 ? 0 : this.list.scrollLeft + width;
      this.list.scrollTo({ left: to, behavior: 'smooth' });
    }
  }
  customElements.define('trust-strip', TrustStrip);

  /* ---------- product reviews: summary, rating filters, sorting, show more ---------- */
  // Sample reviews are allowed only in the theme editor, an unpublished theme or the local preview:
  // the published theme never loads them, whatever the setting says.
  const samplesAllowed = () => {
    const S = window.Shopify;
    if (!S || !S.theme || !S.theme.role) return false; // unknown: treat as published
    return !!S.designMode || S.theme.role !== 'main';
  };
  class ProductReviews extends HTMLElement {
    async connectedCallback() {
      // data-t-* attributes hold the translated strings: data-t-count-one -> this.t.countOne
      this.t = { ...this.dataset };
      Object.keys(this.dataset).forEach((k) => {
        if (/^t[A-Z]/.test(k)) this.t[k.charAt(1).toLowerCase() + k.slice(2)] = this.dataset[k];
      });
      this.perPage = Number(this.t.perPage) || 6;
      this.shown = this.perPage;
      this.filter = 0;
      this.sort = 'recent';
      let reviews = [];
      try { reviews = JSON.parse(this.querySelector('[data-reviews-real]').textContent || '[]'); } catch (e) { reviews = []; }
      this.sample = false;
      if (!reviews.length && this.t.demoUrl && samplesAllowed()) {
        try {
          const data = await fetch(this.t.demoUrl).then((r) => r.json());
          reviews = (data[this.t.handle] || []).map((r) => ({ ...r, verified: false }));
          this.sample = reviews.length > 0;
        } catch (e) { reviews = []; }
      }
      this.reviews = reviews.filter((r) => r.rating >= 1 && r.rating <= 5);
      this.body = this.querySelector('[data-body]');
      if (!this.reviews.length) {
        if (this.t.showEmpty === 'true') this.querySelector('[data-empty]').hidden = false;
        return;
      }
      this.querySelector('[data-demo-note]').hidden = !this.sample;
      this.body.hidden = false;
      this.list = this.querySelector('[data-list]');
      this.more = this.querySelector('[data-more]');
      this.status = this.querySelector('[data-status]');
      this.template = this.querySelector('template[data-card]');
      this.summary();
      this.querySelector('[data-sort]').addEventListener('change', (e) => { this.sort = e.target.value; this.shown = this.perPage; this.render(false); });
      this.more.addEventListener('click', () => { const from = this.shown; this.shown += this.perPage; this.render(true, from); });
      this.render(false);
    }
    plural(n, one, other) { return (n === 1 ? one : other).replace(/\d+/, String(n)); }
    summary() {
      const n = this.reviews.length;
      const avg = this.reviews.reduce((a, r) => a + r.rating, 0) / n;
      const avgText = avg.toFixed(1);
      this.querySelector('[data-average]').textContent = avgText;
      const big = this.querySelector('[data-average-stars]');
      big.style.setProperty('--rating', avg.toFixed(2));
      this.querySelector('[data-count]').textContent = this.plural(n, this.t.countOne, this.t.countOther);
      this.querySelector('[data-average]').setAttribute('aria-label', this.t.rated.replace('%N%', avgText));
      // rating bars double as filters; chips repeat them where the bars are out of view
      const counts = [5, 4, 3, 2, 1].map((star) => [star, this.reviews.filter((r) => r.rating === star).length]);
      const bars = this.querySelector('[data-bars]');
      const chips = this.querySelector('[data-filters]');
      bars.innerHTML = '';
      chips.innerHTML = '';
      const chip = (value, label) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'chip';
        b.dataset.filter = value;
        b.textContent = label;
        b.setAttribute('aria-pressed', value === 0 ? 'true' : 'false');
        chips.append(b);
        return b;
      };
      chip(0, `${this.t.all} (${n})`);
      counts.forEach(([star, count]) => {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'reviews__bar';
        b.dataset.filter = String(star);
        b.setAttribute('aria-pressed', 'false');
        b.disabled = count === 0;
        const label = this.plural(star, this.t.starOne, this.t.starOther);
        b.setAttribute('aria-label', `${label}: ${count}`);
        b.innerHTML = `<span>${star} ★</span><span class="reviews__track"><span class="reviews__fill" style="--w:${Math.round((count / n) * 100)}%"></span></span><span class="reviews__bar-count">${count}</span>`;
        li.append(b);
        bars.append(li);
        const c = chip(star, `${star} ★ (${count})`);
        c.disabled = count === 0;
        c.setAttribute('aria-label', `${label} (${count})`);
      });
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-filter]');
        if (!btn || btn.disabled) return;
        const value = Number(btn.dataset.filter);
        this.filter = this.filter === value && value !== 0 ? 0 : value;
        this.shown = this.perPage;
        this.querySelectorAll('[data-filter]').forEach((b) => b.setAttribute('aria-pressed', Number(b.dataset.filter) === this.filter ? 'true' : 'false'));
        this.render(false);
      });
      this.lineText = (this.sample ? this.t.summaryDemo : this.t.summary).replace('%AVG%', avgText).replace('%N%', n);
      this.lineRating = avg.toFixed(2);
      this.fillLine();
    }
    // the line under the product title (filled again when the editor re-renders that section)
    fillLine() {
      const line = document.querySelector('[data-review-summary]');
      if (!line || !line.hidden || !this.lineText) return;
      line.querySelector('[data-review-stars]').style.setProperty('--rating', this.lineRating);
      line.querySelector('[data-review-text]').textContent = this.lineText;
      line.hidden = false;
    }
    sorted() {
      const list = this.reviews.filter((r) => !this.filter || r.rating === this.filter);
      const by = {
        recent: (a, b) => (b.date || '').localeCompare(a.date || ''),
        high: (a, b) => b.rating - a.rating || (b.date || '').localeCompare(a.date || ''),
        low: (a, b) => a.rating - b.rating || (b.date || '').localeCompare(a.date || ''),
        long: (a, b) => (b.body || '').length - (a.body || '').length,
      }[this.sort];
      return list.sort(by);
    }
    card(r) {
      const li = this.template.content.firstElementChild.cloneNode(true);
      li.querySelector('[data-stars]').style.setProperty('--rating', r.rating);
      li.querySelector('[data-stars-label]').textContent = this.t.rated.replace('%N%', r.rating);
      const tag = li.querySelector('[data-tag]');
      if (this.sample) { tag.textContent = this.t.sample; tag.hidden = false; }
      else if (r.verified === true) { tag.textContent = this.t.verified; tag.hidden = false; }
      const title = li.querySelector('[data-title]');
      if (r.title) title.textContent = r.title; else title.remove();
      li.querySelector('[data-text]').textContent = r.body || '';
      li.querySelector('[data-author]').textContent = r.author || '';
      li.querySelector('[data-variant]').textContent = r.variant || '';
      const time = li.querySelector('[data-date]');
      if (r.date) {
        time.dateTime = r.date;
        time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${r.date}T12:00:00`));
      }
      return li;
    }
    render(appending, from = 0) {
      const list = this.sorted();
      const visible = list.slice(0, this.shown);
      if (!appending) this.list.innerHTML = '';
      const start = appending ? from : 0;
      let first = null;
      visible.slice(start).forEach((r) => {
        const li = this.card(r);
        if (appending) li.classList.add('is-new');
        if (!first) first = li;
        this.list.append(li);
      });
      if (appending && first) first.focus({ preventScroll: false });
      this.querySelector('[data-none]').hidden = list.length > 0;
      const left = list.length - visible.length;
      this.more.hidden = left <= 0;
      this.more.textContent = this.t.more.replace('%N%', Math.min(left, this.perPage));
      this.status.textContent = this.t.status.replace('%SHOWN%', visible.length).replace('%TOTAL%', list.length);
    }
  }
  customElements.define('product-reviews', ProductReviews);

  /* ---------- kit builder ---------- */
  class KitBuilder extends HTMLElement {
    connectedCallback() {
      this.totalEl = this.querySelector('[data-kit-total]');
      this.countEl = this.querySelector('[data-kit-count]');
      this.listEl = this.querySelector('[data-kit-list]');
      this.button = this.querySelector('[data-kit-add]');
      this.emptyEl = this.querySelector('[data-kit-empty]');
      this.status = this.querySelector('[data-kit-status]');
      this.message = this.querySelector('[data-kit-message]');
      this.tray = this.querySelector('[data-kit-tray]');
      this.barTray = this.querySelector('[data-kit-tray-bar]');
      this.barCount = this.querySelector('[data-kit-count-bar]');
      this.barTotal = this.querySelector('[data-kit-total-bar]');
      this.barButton = this.querySelector('[data-kit-add-bar]');
      this.addEventListener('change', () => this.update(true));
      this.button.addEventListener('click', () => this.submit());
      if (this.barButton) this.barButton.addEventListener('click', () => this.submit());
      this.update(false);
    }
    picks() {
      const out = [];
      this.querySelectorAll('[data-kit-item]').forEach((row) => {
        const control = row.querySelector('[data-kit-pick]');
        if (!control || !control.checked) return;
        const select = row.querySelector('[data-kit-variant]');
        const option = select ? select.selectedOptions[0] : null;
        const img = row.querySelector('.kit__media img');
        out.push({
          id: Number(option ? option.value : control.value),
          price: Number(option ? option.dataset.price : control.dataset.price),
          title: row.dataset.title + (option && option.dataset.label ? ` (${option.dataset.label})` : ''),
          kind: row.dataset.kind,
          name: row.dataset.title,
          img: img ? img.currentSrc || img.src : '',
        });
      });
      return out;
    }
    fillTray(tray, picks) {
      if (!tray) return;
      const want = picks.map((p) => String(p.id));
      // keep the thumbnails already shown so only new ones animate in
      [...tray.children].forEach((t) => { if (!want.includes(t.dataset.id)) t.remove(); });
      picks.forEach((p, i) => {
        let t = tray.querySelector(`[data-id="${p.id}"]`);
        if (!t) {
          t = document.createElement('span');
          t.className = 'kit__thumb';
          t.dataset.id = String(p.id);
          if (p.img) { const im = document.createElement('img'); im.src = p.img; im.alt = ''; t.append(im); }
        }
        if (tray.children[i] !== t) tray.insertBefore(t, tray.children[i] || null);
      });
    }
    update(announceChange) {
      const picks = this.picks();
      const total = picks.reduce((s, p) => s + p.price, 0);
      const countText = (picks.length === 1 ? this.dataset.one : this.dataset.other).replace(/\d+/, String(picks.length));
      this.totalEl.textContent = formatMoney(total);
      if (this.countEl) this.countEl.textContent = countText;
      if (this.barCount) this.barCount.textContent = countText;
      if (this.barTotal) this.barTotal.textContent = formatMoney(total);
      if (this.barButton) this.barButton.disabled = picks.length === 0;
      if (announceChange && this.status) this.status.textContent = this.status.dataset.label.replace('%COUNT%', picks.length).replace('%TOTAL%', formatMoney(total));
      if (announceChange && this.message) this.message.hidden = true;
      this.listEl.innerHTML = '';
      picks.forEach((p) => {
        const li = document.createElement('li');
        const name = document.createElement('span');
        name.textContent = p.title;
        const price = document.createElement('span');
        price.className = 'tabular';
        price.textContent = formatMoney(p.price);
        li.append(name, price);
        this.listEl.append(li);
      });
      this.fillTray(this.tray, picks);
      this.fillTray(this.barTray, picks);
      if (this.emptyEl) this.emptyEl.hidden = picks.length > 0;
      this.button.disabled = picks.length === 0;
      this.querySelectorAll('[data-kit-item]').forEach((row) => {
        const pick = row.querySelector('[data-kit-pick]');
        row.classList.toggle('is-picked', !!(pick && pick.checked));
      });
      // accessories that fit the lamps name the lamp picked
      const lamps = picks.filter((p) => p.kind === 'lamp').map((p) => p.name);
      this.querySelectorAll('[data-fits="lamps"] [data-hint]').forEach((hint) => {
        if (lamps.length === 1) hint.textContent = this.dataset.fitsOne.replace('%TITLE%', lamps[0]);
        else if (lamps.length > 1) hint.textContent = this.dataset.fitsBoth;
        else hint.textContent = hint.dataset.hint;
      });
    }
    // One request for the whole kit. If Shopify refuses it (an item sold out, a quantity limit),
    // compare the cart with how it was before, add what is missing one by one, and say exactly
    // what went in and what did not.
    async addKit(picks) {
      const items = picks.map((p) => ({ id: p.id, quantity: 1 }));
      const cartNow = () => fetch(`${DYB.routes.cart}.js`).then((r) => r.json());
      const qty = (cart, id) => cart.items.filter((i) => i.variant_id === id).reduce((n, i) => n + i.quantity, 0);
      const before = await cartNow().catch(() => null);
      try {
        await addItems(items);
        return { added: picks, failed: [] };
      } catch (err) {
        const after = await cartNow().catch(() => null);
        const added = [];
        const failed = [];
        for (const p of picks) {
          if (after && (before ? qty(after, p.id) > qty(before, p.id) : qty(after, p.id) > 0)) { added.push(p); continue; }
          try {
            await addItems([{ id: p.id, quantity: 1 }]);
            added.push(p);
          } catch (e) {
            failed.push({ ...p, reason: e.message });
          }
        }
        return { added, failed };
      }
    }
    async submit() {
      const picks = this.picks();
      if (!picks.length || this.button.getAttribute('aria-busy') === 'true') return;
      const label = this.button.querySelector('[data-label]');
      if (!label.dataset.default) label.dataset.default = label.textContent;
      window.clearTimeout(this.labelTimer);
      this.button.setAttribute('aria-busy', 'true');
      if (this.message) this.message.hidden = true;
      label.textContent = DYB.strings.adding;
      const { added, failed } = await this.addKit(picks);
      this.button.removeAttribute('aria-busy');
      if (!failed.length) {
        label.textContent = this.dataset.added || DYB.strings.added;
        afterAdd(this.button);
        announce(DYB.strings.addedToCart);
      } else {
        label.textContent = label.dataset.default;
        const list = failed.map((f) => (f.reason ? `${f.title}: ${f.reason}` : f.title)).join('; ');
        const text = added.length
          ? this.dataset.partial.replace('%ADDED%', added.length).replace('%TOTAL%', picks.length).replace('%FAILED%', list)
          : this.dataset.failed.replace('%FAILED%', list);
        if (this.message) { this.message.textContent = text; this.message.hidden = false; }
        else announce(text);
      }
      this.labelTimer = window.setTimeout(() => { label.textContent = label.dataset.default; }, 1800);
    }
  }
  customElements.define('kit-builder', KitBuilder);

  /* ---------- collection filter chips (all products show without JS) ---------- */
  class CollectionFilter extends HTMLElement {
    connectedCallback() {
      this.chips = [...this.querySelectorAll('[data-filter]')];
      this.items = [...this.querySelectorAll('[data-tags]')];
      this.count = this.querySelector('[data-count]');
      this.template = this.count ? this.count.textContent : '';
      this.chips.forEach((chip) => chip.addEventListener('click', () => this.apply(chip)));
    }
    apply(chip) {
      const tags = chip.dataset.filter ? chip.dataset.filter.split(',') : [];
      let shown = 0;
      this.items.forEach((item) => {
        const own = item.dataset.tags.split(',');
        const match = !tags.length || tags.some((t) => own.includes(t));
        item.hidden = !match;
        if (match) shown += 1;
      });
      this.chips.forEach((c) => { const on = c === chip; c.classList.toggle('is-active', on); c.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      if (this.count) this.count.textContent = (shown === 1 ? this.count.dataset.one : this.count.dataset.other).replace(/\d+/, String(shown));
    }
  }
  customElements.define('collection-filter', CollectionFilter);

  /* ---------- reveal on scroll ---------- */
  const initReveal = () => {
    const items = document.querySelectorAll('.reveal:not(.is-in)');
    if (!document.body.hasAttribute('data-reveal-on') || reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    document.querySelectorAll('[data-stagger]').forEach((group) => {
      [...group.querySelectorAll(':scope > .reveal, :scope > * > .reveal')].forEach((el, i) => el.style.setProperty('--i', i % 8));
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => observer.observe(el));
  };

  /* ---------- hero light: play once per visit ---------- */
  const initHeroLight = () => {
    const hero = document.querySelector('[data-hero-light]');
    if (!hero) return;
    if (window.Shopify && window.Shopify.designMode) { hero.classList.add('is-lit'); return; }
    let seen = false;
    try { seen = window.sessionStorage.getItem('dyb6-lit') === '1'; window.sessionStorage.setItem('dyb6-lit', '1'); } catch (e) { seen = false; }
    if (seen || reduceMotion.matches) hero.classList.add('is-lit');
    else window.requestAnimationFrame(() => hero.classList.add('is-lighting'));
  };

  const init = () => { initReveal(); initHeroLight(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initHeroLight();
    document.querySelectorAll('product-reviews').forEach((r) => r.fillLine && r.fillLine());
  });
  // theme editor: selecting a block shows it (the second lamp on the phone switch, a trust line)
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const panel = block.closest && block.closest('[data-panel]');
    const chooser = panel && panel.closest('lamp-chooser');
    if (chooser) chooser.activate([...chooser.querySelectorAll('[data-panel]')].indexOf(panel));
    const trust = block.closest && block.closest('[data-trust-list]');
    if (trust) {
      trust.closest('trust-strip').stopped = true;
      trust.scrollTo({ left: block.offsetLeft - trust.offsetLeft, behavior: 'auto' });
    }
  });
})();
